import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import DishCard from "@/components/DishCard";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/components/Seo";
import { SmallQuantityEnquiry } from "@/components/SmallQuantityEnquiry";

export default function Menu() {
  useSeo({
    title: "Indian Catering Menu — 130+ Veg Dishes | Tezz Khana Mumbai",
    description: "Browse the full Tezz Khana catering menu — starters, soups, salads, mains, live counters, desserts, sweets and beverages, freshly prepared for events across Mumbai.",
    path: "/menu",
  });
  const { t } = useApp();
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "";
  const initialQ = params.get("q") || "";

  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [search, setSearch] = useState(initialQ);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/categories");
        setCategories(data || []);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (activeCat) params.category_id = activeCat;
        if (search) params.search = search;
        const { data } = await api.get("/dishes", { params });
        setDishes(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeCat, search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (activeCat) next.set("category", activeCat);
    if (search) next.set("q", search);
    setParams(next, { replace: true });
  }, [activeCat, search, setParams]);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c));
    return m;
  }, [categories]);

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">{t("menu.title")}</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">Our Catering Menu</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("menu.subtitle")} Freshly prepared Indian veg catering, served across Mumbai.</p>
      </div>

      <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-border/70 bg-card p-4 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            data-testid="menu-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search.placeholder")}
            className="w-full bg-transparent text-sm focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="menu-category-tabs">
        <button
          data-testid="menu-cat-all"
          onClick={() => setActiveCat("")}
          className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
            activeCat === ""
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border/70 bg-background text-secondary/80 hover:bg-muted"
          }`}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`menu-cat-${c.slug}`}
            onClick={() => setActiveCat(c.id)}
            className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
              activeCat === c.id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/70 bg-background text-secondary/80 hover:bg-muted"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 animate-pulse rounded-2xl border border-border/70 bg-muted/60" />
            ))}
          </div>
        ) : dishes.length === 0 ? (
          <div
            data-testid="menu-empty-state"
            className="rounded-2xl border border-dashed border-border/70 bg-background p-12 text-center text-sm text-muted-foreground"
          >
            {activeCat || search ? t("search.no_results") : t("menu.empty_all")}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {dishes.map((d) => (
              <DishCard key={d.id} dish={d} categoryName={catById[d.category_id]?.name} />
            ))}
          </div>
        )}
      </div>

      <SmallQuantityEnquiry />
    </div>
  );
}
